/**
 * @file 人拉人接口
 * @refs https://cooper.didichuxing.com/docs2/document/2202975275981
 */
import $ from 'zepto'
import { URL } from '@/utils/dom'
import { getStore, setStore } from '@/store/store-standard'
import { APPLY_CHANNEL } from '@/types'
import { isMgm } from '@/utils/dom'
import { Login } from '@/utils/login-lib'
import Omega from 'Omega'

const buildParams = (): { [key: string]: any } => {
  return {
    request_type: 2,
    activity_id: URL.get('activity_id'),
    activity_type: URL.get('activity_type'),
    link_source: 1,
    uid: URL.get('uid'),
    location_country: URL.get('location_country') || 'MX',
    from_source: URL.get('from_source'),
    lang: URL.get('lang') || 'es-MX',
    locale: URL.get('locale') || 'es_MX',
    country_calling_code:
      URL.get('country_calling_code') || '+52',
    city_id:
      URL.get('city_id') ||
      getStore('httpHomeIndex')?.cityId ||
      '52090100',
    url_id: URL.get('url_id'),
    applyChannel: APPLY_CHANNEL,
  }
}

const { env, cluster } = window.__INIT_STATE__

const ENV = {
  'prod:us': 'https://growth.didiglobal.com/litchi',
  'prod:mx': 'https://growth.didifinance.com/litchi',
  'pre:us':
    'http://ibg_growth_pre.intra.didiglobal.com/litchi',
  'local:us':
    'https://dsim000-api.intra.xiaojukeji.com/litchi',
  'sim:us':
    'https://dsim000-api.intra.xiaojukeji.com/litchi',
  get() {
    const key = `${env}:${cluster}`
    if (ENV[key]) {
      return ENV[key]
    }
    return ''
  },
} as {
  [key: string]: any
  get: () => string
}

const getOmegaOmgId = (): string => {
  try {
    return (
      Omega?.context?.omgId ??
      (window as Window & { Omega?: { context?: { omgId?: string } } })
        .Omega?.context?.omgId ??
      ''
    )
  } catch {
    return ''
  }
}

const omegaOidHeaders = (): { oid: string } => ({
  oid: getOmegaOmgId(),
})

/**
 * 查询活动信息
 * @returns
 */
interface IActivity {
  errno: number
  errmsg: string
  data: {
    [key: string]: unknown
  }
}
export const httpActivityInfo = (): Promise<IActivity> => {
  const params = buildParams()
  // -1 非mgm渠道
  if (!isMgm()) {
    return Promise.resolve({
      errno: -1,
      data: {},
      errmsg: '',
    })
  }

  return new Promise((resolve) =>
    $.ajax({
      url: `${ENV.get()}/api/common/activity_info`,
      type: 'GET',
      data: {
        fields:
          'user_info,base_info,material.share_page,material.extra_info',
        ...params,
      },
      headers: omegaOidHeaders(),
      success(data: IActivity) {
        if (data.errno === 0) {
          setStore('mgm', data.data)
        }
        resolve(data)
      },
    })
  )
}

/**
 * 活动绑定
 * ibt接口
 */
export const httpActivityJoin = (append: {
  [key: string]: unknown
}): Promise<IActivity> => {
  // -1 非mgm渠道
  if (!isMgm()) {
    return Promise.resolve({
      errno: -1,
      data: {},
      errmsg: '',
    })
  }
  const params = buildParams()
  const { ticket, cell, uid } = getStore('user')
  append.token = ticket
  append.cell = cell
  append.referee_uid = uid
  append.country_calling_code =
    getStore('httpHomeIndex').countryCallingCode
  append.extra = JSON.stringify({
    wsgenv: getStore('wsgenv'),
    smsSendTime: getStore('smsSendTime'),
    smsSubmitTime: getStore('smsSubmitTime'),
  })
  return new Promise((resolve) =>
    // $.get(
    //   `${ENV.get()}/api/common/join`,
    //   {
    //     ...params,
    //     ...append,
    //   },
    //   (data: IActivity) => resolve(data)
    // )
    $.ajax({
      url: `${ENV.get()}/api/common/join`,
      type: 'POST',
      data: {
        ...params,
        ...append,
      },
      headers: {
        ...omegaOidHeaders(),
        'didi-fintech-header-content': JSON.stringify({
          token: ticket || '',
          fintech_token: '',
        }),
      },
      success(data: IActivity) {
        resolve(data)
      },
    })
  )
}

/**
 * 查询用户是否已成功绑定券
 */
export const httpActivityQuery = (): Promise<IActivity> => {
  const REJECT_RESPONSE = {
    errno: -1,
    data: {},
    errmsg: '',
  }
  // -1 非mgm渠道
  if (!isMgm()) {
    return Promise.resolve(REJECT_RESPONSE)
  }
  const { uid } = getStore('user')
  const params = buildParams()
  return new Promise((resolve) =>
    $.ajax({
      url: `${ENV.get()}/api/common/reward_status`,
      type: 'GET',
      data: {
        ...params,
        referee_uid: uid,
      },
      headers: omegaOidHeaders(),
      success(data: IActivity) {
        if (data?.data?.reward_success) {
          return resolve(data)
        }
        return resolve(REJECT_RESPONSE)
      },
    })
  )
}

interface ICommon {
  errorCode: number
  errorMsg: string
  data: any
}

export const httpHomeIndex = (): Promise<ICommon> => {
  const token = Login.getTicket()
  const nextData = {
    errorCode: -1,
    errorMsg: '',
    data: {},
  }
  // 如果两者都没有，则认为没登录
  if (!token) {
    nextData.errorMsg = 'User login fail.'
    return Promise.resolve(nextData)
  }
  return new Promise((resolve) =>
    $.ajax({
      url: '/api/home/outcast/index',
      type: 'POST',
      data: {
        token,
      },
      headers: {
        'didi-fintech-header-content': JSON.stringify({
          token: token,
          fintech_token: '',
        }),
      },
      success(data: ICommon) {
        // 异常兜底case1，没有任何返回值
        if (!data) {
          return resolve(nextData)
        }
        // 异常兜底case2，用户状态异常，但是data返回了null，后面兜不住
        if (data.errorCode !== 10000 && !data.data) {
          nextData.errorCode = data.errorCode
          return resolve(nextData)
        }
        if (data.data) {
          data.data.creditDetail =
            data.data.creditDetail || {}
        }
        resolve(data)
      },
      error(error: ICommon) {
        nextData.errorCode = error.errorCode || -1
        resolve(nextData)
      },
    })
  )
}
